import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { ProposedSolutionStep } from "Infrastructure/ApiZfp/Schema";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, array, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export type CustomAmountCoverageFormField = {
  readonly name: string;
  readonly currentAmount: number;
  readonly recommendedAmount: number;
};

export type ImmovableContractFormType = {
  readonly partnerId: string;
  readonly newPartnerId?: string;
  readonly contractAnniversaryDate?: string;
  readonly insuranceAmount?: number;
  readonly newInsuranceAmount?: number;
  readonly frequency: string;
  readonly newFrequency: string;
  readonly propertyInsuranceCurentAmount?: number;
  readonly propertyInsuranceRecommendedAmount?: number;
  readonly householdInsuranceCurentAmount?: number;
  readonly householdInsuranceRecommendedAmount?: number;
  readonly adjacentBuildingsInsuranceCurentAmount?: number;
  readonly adjacentBuildingsInsuranceRecommendedAmount?: number;
  readonly ownerLiabilityInsuranceCurentAmount?: number;
  readonly ownerLiabilityInsuranceRecommendedAmount?: number;
  readonly liabilityInsuranceCurentAmount?: number;
  readonly liabilityInsuranceRecommendedAmount?: number;
  readonly proposedSolutionStepType: string;
  readonly customAmountCoverages: CustomAmountCoverageFormField[];
  readonly note?: string;
};

const requiredWhenNotKeeping = (t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string) =>
  number().when("proposedSolutionStepType", (proposedSolutionStepType, schema) => {
    return proposedSolutionStepType[0] === ProposedSolutionStep.keep ? schema : schema.required(t(Resources.Validation.Required));
  });

export const getImmovableContractFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<ImmovableContractFormType> = (t) => {
  return object({
    partnerId: string().required(t(Resources.Validation.Required)),
    newPartnerId: string().when("proposedSolutionStepType", (proposedSolutionStepType, schema) => {
      return proposedSolutionStepType[0] === ProposedSolutionStep.keep ? schema : schema.required(t(Resources.Validation.Required));
    }),
    contractAnniversaryDate: string(),
    insuranceAmount: number(),
    newInsuranceAmount: requiredWhenNotKeeping(t),
    frequency: string().required(t(Resources.Validation.Required)),
    newFrequency: string().required(t(Resources.Validation.Required)),
    propertyInsuranceCurentAmount: number(),
    propertyInsuranceRecommendedAmount: number(),
    householdInsuranceCurentAmount: number(),
    householdInsuranceRecommendedAmount: number(),
    adjacentBuildingsInsuranceCurentAmount: number(),
    adjacentBuildingsInsuranceRecommendedAmount: number(),
    ownerLiabilityInsuranceCurentAmount: number(),
    ownerLiabilityInsuranceRecommendedAmount: number(),
    liabilityInsuranceCurentAmount: number(),
    liabilityInsuranceRecommendedAmount: number(),
    proposedSolutionStepType: string().required(t(Resources.Validation.Required)),
    customAmountCoverages: array(
      object({
        name: string().required(t(Resources.Validation.Required)),
        currentAmount: number().required(t(Resources.Validation.Required)),
        recommendedAmount: number().required(t(Resources.Validation.Required)),
      }).test("at-least-one-amount", t(Resources.Validation.Required), (row) => !!(row?.currentAmount || row?.recommendedAmount)),
    ).required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
  }).defined();
};
